<template>
  <div
    v-if="stack.length"
    data-component="app-toast-container"
  >
    <transition-group
      name="toasts"
      class="toasts"
      tag="div"
    >
      <div
        v-for="toast of stack"
        :key="toast.id"
        class="toast"
        :class="getToastClass(toast)"
      >
        <div class="content">
          <div
            v-if="getIcon(toast)"
            class="icon"
          >
            {{ getIcon(toast) }}
          </div>

          <div class="text">
            {{ toast.message }}
          </div>

          <div
            data-button="clear"
            @click="onCloseToast(toast.id)"
          >
            <or-icon icon="close" />
          </div>
        </div>

        <span
          v-if="toast.details"
          data-button="toggle-details"
          @click="toggleToastDetails(toast)"
        >
          {{ getExpandButtonLabel(toast) }}
        </span>

        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="toast.isExpanded"
          class="details"
          v-html="getToastDetailsAsText(toast)"
        />
      </div>
    </transition-group>
  </div>
</template>

<script lang="ts">
import * as uuid from 'uuid';
import { defineComponent } from 'vue-demi';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import { Toast, TOAST_TYPE } from '../../types';
import {
  UIEventBus,
  SHOW_NOTIFICATION_TOAST,
  CLEAR_NOTIFICATION_TOASTS,
} from '@/common/ui-event-bus';

export default defineComponent({
  name: 'AppToastContainer',
  components: { OrIcon },
  data() {
    return { stack: [] as Toast[] };
  },
  mounted() {
    UIEventBus.$on(SHOW_NOTIFICATION_TOAST, this.onShowToast);
    UIEventBus.$on(CLEAR_NOTIFICATION_TOASTS, this.onClearStack);
  },
  methods: {
    getIcon(toast: Toast) {
      switch (toast.type) {
        case TOAST_TYPE.SUCCESS:
          return '✅';
        case TOAST_TYPE.ERROR:
          return '❌';
        default:
          return '';
      }
    },

    onClearStack() {
      this.stack = [];
    },

    onShowToast(data: Partial<Toast>) {
      const toast = { id: uuid.v4(), isExpanded: false, ...data };
      this.stack.push(toast as Toast);
    },

    onCloseToast(toastId: string) {
      this.stack = this.stack.filter(toast => toast.id !== toastId);
    },

    toggleToastDetails(toast: Toast) {
      this.$set(toast, 'isExpanded', !toast.isExpanded);
    },

    getToastDetailsAsText(toast: Toast) {
      return JSON.stringify(toast.details, null, 2);
    },

    getToastClass(toast: Toast) {
      const type = toast.type || TOAST_TYPE.DEFAULT;
      return { [`is-${type.toLowerCase()}`]: true };
    },

    getExpandButtonLabel(toast: Toast) {
      return toast.isExpanded
        ? this.$t('HITL.common.toast.hide-details')
        : this.$t('HITL.common.toast.show-details');
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='app-toast-container'] {
  --toast-text-color: var(--or-c-on-background);

  display: flex;
  flex-direction: column;
  position: absolute;
  width: 30.5rem;
  max-height: calc(100% - 2 * var(--s-4));
  /* Padding to render box shadow */
  padding: var(--s-3) 0 var(--s-3) var(--s-3);
  right: 0;
  bottom: var(--s-4);
  overflow-y: auto;
  overflow-x: hidden;
  /* Toasts have the highest z-index */
  z-index: 1000;

  .toasts {
    display: flex;
    flex-direction: column;
    gap: var(--s-4);

    &-enter-active,
    &-leave-active {
      transition: transform var(--t-func) var(--t-duration);
    }

    &-enter,
    &-leave-to {
      transform: translateX(100%);
    }
  }

  .toast {
    display: flex;
    flex-direction: column;
    gap: var(--s-3);
    padding: var(--s-4);
    color: var(--or-c-on-background);
    background: var(--or-c-background);
    border-top-left-radius: var(--s-2);
    border-bottom-left-radius: var(--s-2);
    box-shadow: var(--or-shadow);

    &.is-error {
      --toast-text-color: var(--or-c-error);
    }

    .content {
      display: flex;
      gap: var(--s-4);

      .icon,
      [data-button='clear'] {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1.25rem;
        width: 1.25rem;
      }

      .icon {
        font-size: 1rem;
      }

      .text {
        color: var(--toast-text-color);
        word-break: break-word;
      }

      [data-button='clear'] {
        margin-left: auto;
        color: var(--or-c-outline);
        user-select: none;
        cursor: pointer;

        &:hover {
          color: var(--or-c-on-background);
        }
      }
    }

    [data-button='toggle-details'] {
      margin-left: var(--s-6);
      color: var(--or-c-primary);
      cursor: pointer;
      user-select: none;

      &:hover {
        color: var(--or-c-primary-hover);
      }
    }

    .details {
      padding: var(--s-3) var(--s-4);
      white-space: pre;
      overflow: auto;
      background: var(--or-c-surface-1);
      border-radius: var(--s-2);
    }
  }
}
</style>
